import { startOfDay } from "date-fns";
import { useState } from "react";
import { Plus } from "@/components/icons";
import { Button, DatePicker, Input, Textarea } from "@/components/kit";
import {
  Field as FieldLayout,
  FieldContent,
  FieldLabel,
} from "@/components/ui/field";
import {
  type CreateExpenseDTO,
  type ExpenseCategoryDTO,
  expenseCategories,
} from "@/modules/expenses/domain/contracts";
import type { ExpenseDTO } from "@/modules/expenses/domain/types";
import { EXPENSE_CATEGORY_LABELS } from "@/modules/expenses/presentation/lib/category-labels";
import {
  useProductCategoriesQuery,
  useProductsQuery,
} from "@/modules/products/presentation/api/queries";
import { formatDateForInput } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const NO_MAPPING = "__none";

type ExpenseFormState = {
  category: ExpenseCategoryDTO;
  amount: string;
  spentAt: string;
  title: string;
  vendor: string;
  platform: string;
  campaignName: string;
  mappedProductId: string;
  mappedCategoryId: string;
  note: string;
};

function defaultForm(): ExpenseFormState {
  return {
    category: "facebook_ads",
    amount: "",
    spentAt: formatDateForInput(new Date()),
    title: "Facebook Ads",
    vendor: "Meta",
    platform: "facebook",
    campaignName: "",
    mappedProductId: "",
    mappedCategoryId: "",
    note: "",
  };
}

function formFromExpense(expense: ExpenseDTO): ExpenseFormState {
  return {
    category: expense.category,
    amount: String(Number(expense.amount)),
    spentAt: formatDateForInput(expense.spentAt),
    title: expense.title,
    vendor: expense.vendor ?? "",
    platform: expense.platform ?? "",
    campaignName: expense.campaignName ?? "",
    mappedProductId: expense.mappedProductId ?? "",
    mappedCategoryId: expense.mappedCategoryId ?? "",
    note: expense.note ?? "",
  };
}

function toPayload(form: ExpenseFormState): CreateExpenseDTO {
  return {
    category: form.category,
    amount: Number(form.amount) || 0,
    spentAt: startOfDay(new Date(form.spentAt)).toISOString(),
    title: form.title.trim(),
    vendor: form.vendor.trim() || null,
    platform: form.platform.trim() || null,
    campaignName: form.campaignName.trim() || null,
    mappedProductId: form.mappedProductId || null,
    mappedCategoryId: form.mappedCategoryId || null,
    note: form.note.trim() || null,
  };
}

type ExpenseFormProps = {
  defaultValues?: ExpenseDTO;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (values: CreateExpenseDTO) => Promise<void> | void;
};

export function ExpenseForm({
  defaultValues,
  submitting,
  submitLabel = "ເພີ່ມລາຍຈ່າຍ",
  onSubmit,
}: ExpenseFormProps) {
  const [form, setForm] = useState<ExpenseFormState>(() =>
    defaultValues ? formFromExpense(defaultValues) : defaultForm(),
  );
  const categories = useProductCategoriesQuery();
  const products = useProductsQuery({ includeInactive: true });
  const isFacebook = form.category === "facebook_ads";

  function patchForm(patch: Partial<ExpenseFormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  function handleCategory(category: ExpenseCategoryDTO) {
    patchForm({
      category,
      ...(category === "facebook_ads"
        ? {
            title: form.title || "Facebook Ads",
            vendor: form.vendor || "Meta",
            platform: form.platform || "facebook",
          }
        : {}),
    });
  }

  async function handleSubmit() {
    await onSubmit(toPayload(form));
  }

  return (
    <section className="brand-card flex flex-col gap-4 p-5">
      <div>
        <h3 className="font-display text-lg">ຂໍ້ມູນລາຍຈ່າຍ</h3>
        <p className="text-muted-foreground text-sm">
          ບັນທຶກຄ່າແອດ Facebook ຫຼື ລາຍຈ່າຍຮ້ານ
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FieldLayout>
          <FieldLabel>ປະເພດ</FieldLabel>
          <FieldContent>
            <SimpleSelect
              value={form.category}
              onValueChange={(value) =>
                handleCategory(value as ExpenseCategoryDTO)
              }
              options={expenseCategories.map((category) => ({
                value: category,
                label: EXPENSE_CATEGORY_LABELS[category],
              }))}
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel>ວັນທີ່ໃຊ້ຈ່າຍ</FieldLabel>
          <FieldContent>
            <DatePicker
              value={form.spentAt ? new Date(form.spentAt) : null}
              onChange={(date) =>
                patchForm({
                  spentAt: date ? formatDateForInput(date) : "",
                })
              }
              captionLayout="dropdown"
              fromYear={2024}
              toYear={new Date().getFullYear() + 1}
              className="w-full"
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="expense-title">ຊື່ລາຍການ</FieldLabel>
          <FieldContent>
            <Input
              id="expense-title"
              value={form.title}
              onChange={(e) => patchForm({ title: e.target.value })}
              placeholder="FB Ads Campaign / ກ່ອງພັດສະດຸ..."
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="expense-amount">ຈຳນວນເງິນ (₭)</FieldLabel>
          <FieldContent>
            <Input
              id="expense-amount"
              value={form.amount}
              onChange={(e) =>
                patchForm({ amount: e.target.value.replace(/[^0-9.]/g, "") })
              }
              inputMode="numeric"
              placeholder="0"
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="expense-vendor">Vendor</FieldLabel>
          <FieldContent>
            <Input
              id="expense-vendor"
              value={form.vendor}
              onChange={(e) => patchForm({ vendor: e.target.value })}
              placeholder={isFacebook ? "Meta" : "ຊື່ຮ້ານ / ຜູ້ຮັບເງິນ"}
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="expense-platform">Platform</FieldLabel>
          <FieldContent>
            <Input
              id="expense-platform"
              value={form.platform}
              onChange={(e) => patchForm({ platform: e.target.value })}
              placeholder={isFacebook ? "facebook" : "facebook / tiktok / ..."}
            />
          </FieldContent>
        </FieldLayout>
      </div>

      {isFacebook ? (
        <FieldLayout>
          <FieldLabel htmlFor="expense-campaign">Campaign</FieldLabel>
          <FieldContent>
            <Input
              id="expense-campaign"
              value={form.campaignName}
              onChange={(e) => patchForm({ campaignName: e.target.value })}
              placeholder="June COD Campaign"
            />
          </FieldContent>
        </FieldLayout>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <FieldLayout>
          <FieldLabel>Mapping ຫມວດສິນຄ້າ (optional)</FieldLabel>
          <FieldContent>
            <SimpleSelect
              value={form.mappedCategoryId || NO_MAPPING}
              onValueChange={(value) =>
                patchForm({
                  mappedCategoryId: value === NO_MAPPING ? "" : value,
                })
              }
              options={[
                { value: NO_MAPPING, label: "ບໍ່ຜູກຫມວດ" },
                ...(categories.data?.data ?? []).map((category) => ({
                  value: category.id,
                  label: category.name,
                })),
              ]}
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel>Mapping ສິນຄ້າ (optional)</FieldLabel>
          <FieldContent>
            <SimpleSelect
              value={form.mappedProductId || NO_MAPPING}
              onValueChange={(value) =>
                patchForm({
                  mappedProductId: value === NO_MAPPING ? "" : value,
                })
              }
              options={[
                { value: NO_MAPPING, label: "ບໍ່ຜູກສິນຄ້າ" },
                ...(products.data?.data ?? []).map((product) => ({
                  value: product.id,
                  label: product.name,
                })),
              ]}
            />
          </FieldContent>
        </FieldLayout>
      </div>

      <FieldLayout>
        <FieldLabel htmlFor="expense-note">ໝາຍເຫດ</FieldLabel>
        <FieldContent>
          <Textarea
            id="expense-note"
            value={form.note}
            onChange={(e) => patchForm({ note: e.target.value })}
            rows={3}
            placeholder="ໝາຍເຫດເພີ່ມເຕີມ..."
          />
        </FieldContent>
      </FieldLayout>

      <Button
        className="w-full rounded-full sm:w-fit"
        disabled={submitting || !form.title.trim() || Number(form.amount) <= 0}
        onClick={handleSubmit}
      >
        <Plus data-icon="inline-start" />
        {submitting ? "ກຳລັງບັນທຶກ..." : submitLabel}
      </Button>
    </section>
  );
}
